import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Button, ControlGroup, Popup } from '@ui';
import { translate } from '@utils/i18n';
import useGlobalScope from './hooks/useGlobalScope';
import useTerritorySelection from './hooks/useTerritorySelection';
import { readTerritoriesCache, writeTerritoriesCache } from './helpers/territoriesCache';
import './GlobalScopeSelectorStyles.scss';
const namespace = 'features.global_scope_selector';
const ALL_TERRITORIES_KEY = 'all';
/** Sentinel ID used to represent buildings with no assigned territory (maps to `territory_id IS NULL` on the backend). */
const NOT_ASSIGNED_TERRITORY_ID = 0;
function GlobalScopeSelector({ hasTerritoriesLimitations = false }) {
    const anchor = React.useRef(null);
    const instanceAnchor = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    const [openInstanceSelector, setOpenInstanceSelector] = React.useState(false);
    const [territorySearch, setTerritorySearch] = React.useState('');
    const currentSubdomain = React.useMemo(() => {
        const { hostname } = window.location;
        const parts = hostname.split('.');
        if (parts.length > 2) {
            return parts[0];
        }
        return 'dev';
    }, []);
    const { data: scopeSettings, isLoading, error } = useGlobalScope();
    const { territorySelections, getSelectedTerritories, clearSelection, setMultipleTerritories, removeStoredSelection } = useTerritorySelection();
    const hasInitializedRef = useRef(false);
    const [cachedTerritories, setCachedTerritories] = useState(() => { var _a; return (_a = readTerritoriesCache()) !== null && _a !== void 0 ? _a : []; });
    useEffect(() => {
        var _a;
        if ((_a = scopeSettings === null || scopeSettings === void 0 ? void 0 : scopeSettings.territories) === null || _a === void 0 ? void 0 : _a.length) {
            writeTerritoriesCache(scopeSettings.territories);
            setCachedTerritories(scopeSettings.territories);
        }
    }, [scopeSettings === null || scopeSettings === void 0 ? void 0 : scopeSettings.territories]);
    const [initialSelection, setInitialSelection] = useState([]);
    const handleClose = useCallback(() => {
        setMultipleTerritories(initialSelection, currentSubdomain);
        setTerritorySearch('');
        setOpen(false);
        setOpenInstanceSelector(false);
    }, [initialSelection, currentSubdomain, setMultipleTerritories]);
    const filterOptions = useMemo(() => {
        const territories = (scopeSettings === null || scopeSettings === void 0 ? void 0 : scopeSettings.territories) || cachedTerritories;
        return territories.map((territory) => ({ title: territory.name, value: territory.id }));
    }, [scopeSettings === null || scopeSettings === void 0 ? void 0 : scopeSettings.territories, cachedTerritories]);
    const allCheckboxOptions = useMemo(() => (hasTerritoriesLimitations
        ? []
        : [{ title: translate('no_assigned_territory', { namespace }), value: NOT_ASSIGNED_TERRITORY_ID }]).concat(filterOptions), [hasTerritoriesLimitations, filterOptions]);
    const filteredOptions = useMemo(() => {
        if (!territorySearch.trim())
            return allCheckboxOptions;
        const lower = territorySearch.toLowerCase();
        return allCheckboxOptions.filter((opt) => opt.title.toLowerCase().includes(lower));
    }, [allCheckboxOptions, territorySearch]);
    const handleClearAll = useCallback(() => {
        clearSelection(currentSubdomain);
    }, [clearSelection, currentSubdomain]);
    const handleCompanySelect = useCallback((company) => {
        window.open(company.url, '_blank', 'noopener,noreferrer');
        handleClose();
    }, [handleClose]);
    const availableTerritoryIds = useMemo(() => new Set(allCheckboxOptions.map((opt) => opt.value)), [allCheckboxOptions]);
    const rawSelectedIds = useMemo(() => (territorySelections === null || territorySelections === void 0 ? void 0 : territorySelections[currentSubdomain]) || [], [territorySelections, currentSubdomain]);
    // Whether the real (API-backed) territory list has arrived. While loading,
    // `allCheckboxOptions` contains only the NOT_ASSIGNED_TERRITORY_ID placeholder,
    // so filtering rawSelectedIds against it would incorrectly collapse a stored
    // multi-territory selection down to [0] (or []).
    const territoriesLoaded = filterOptions.length > 0;
    const selectedIds = useMemo(() => (territoriesLoaded ? rawSelectedIds.filter((id) => availableTerritoryIds.has(id)) : rawSelectedIds), [rawSelectedIds, availableTerritoryIds, territoriesLoaded]);
    // Reconcile: if a stored id is no longer valid (territory deleted, etc.) drop
    // it and persist the cleaned set. Only run once real territories are present —
    // while still loading the available-id set is incomplete and would clobber
    // a legitimate multi-territory selection.
    useEffect(() => {
        if (!territoriesLoaded)
            return;
        if (rawSelectedIds.length !== selectedIds.length) {
            setMultipleTerritories(selectedIds, currentSubdomain);
        }
    }, [rawSelectedIds, selectedIds, currentSubdomain, setMultipleTerritories, territoriesLoaded]);
    const territoriesSelectedAmount = selectedIds.length;
    const handleToggleOpen = useCallback(() => {
        setInitialSelection(selectedIds);
        setOpen(true);
    }, [selectedIds]);
    const hasSelectionChanges = useMemo(() => {
        if (initialSelection.length !== selectedIds.length)
            return true;
        const initialSet = new Set(initialSelection);
        return selectedIds.some((id) => !initialSet.has(id));
    }, [selectedIds, initialSelection]);
    // ControlGroup uses string values; tiered checkbox with one parent ("All
    // territories") whose children are the individual territory options. The
    // parent state is derived by ControlGroup from the selected children, so
    // `root` stays empty and `children[ALL_TERRITORIES_KEY]` is the source of truth.
    const controlGroupItems = useMemo(() => [
        {
            value: ALL_TERRITORIES_KEY,
            label: translate('all_territories', { namespace }),
            children: filteredOptions.map((opt) => ({
                value: opt.value.toString(),
                label: opt.title
            }))
        }
    ], [filteredOptions]);
    const controlGroupValue = useMemo(() => ({
        root: [],
        children: { [ALL_TERRITORIES_KEY]: selectedIds.map((id) => id.toString()) }
    }), [selectedIds]);
    const handleControlGroupChange = useCallback((next) => {
        const childStrings = next.children[ALL_TERRITORIES_KEY] || [];
        setMultipleTerritories(childStrings.map((s) => parseInt(s, 10)), currentSubdomain);
    }, [setMultipleTerritories, currentSubdomain]);
    const allSelected = territoriesSelectedAmount === allCheckboxOptions.length && allCheckboxOptions.length > 0;
    const handleUpdate = useCallback(() => {
        // When all territories are selected the filter has no effect — remove the cookie
        // key so the server skips the territory subquery entirely. On next load the missing
        // key triggers auto-select-all, so the UI stays consistent.
        if (allSelected) {
            removeStoredSelection(currentSubdomain);
        }
        // Do NOT call setOpen(false) here: closing the popup programmatically fires
        // onClose → handleClose → resets the cookie to initialSelection, reverting the
        // user's changes before the reload. The page reload tears down the UI anyway.
        window.location.reload();
    }, [allSelected, removeStoredSelection, currentSubdomain]);
    const shouldShow = useMemo(() => { var _a; return (((_a = scopeSettings === null || scopeSettings === void 0 ? void 0 : scopeSettings.companies) === null || _a === void 0 ? void 0 : _a.length) || 0) > 0 || filterOptions.length > 0; }, [scopeSettings === null || scopeSettings === void 0 ? void 0 : scopeSettings.companies, filterOptions]);
    // Names shown beside the icon in the trigger button — first 2 selected
    // territories, with non-zero IDs first and "Not Assigned" (id 0) last.
    const displayedTerritoryNames = useMemo(() => {
        const territories = (scopeSettings === null || scopeSettings === void 0 ? void 0 : scopeSettings.territories) || cachedTerritories;
        const ordered = [
            ...selectedIds.filter((id) => id !== NOT_ASSIGNED_TERRITORY_ID),
            ...(selectedIds.includes(NOT_ASSIGNED_TERRITORY_ID) ? [NOT_ASSIGNED_TERRITORY_ID] : [])
        ];
        return ordered
            .slice(0, 2)
            .map((id) => {
            var _a;
            return id === NOT_ASSIGNED_TERRITORY_ID
                ? translate('no_assigned_territory', { namespace })
                : (_a = territories.find((t) => t.id === id)) === null || _a === void 0 ? void 0 : _a.name;
        })
            .filter(Boolean);
    }, [selectedIds, scopeSettings === null || scopeSettings === void 0 ? void 0 : scopeSettings.territories, cachedTerritories]);
    // Auto-select all only on first-ever load (no cookie key yet).
    //
    // Guard against a stale init: wait until the API-backed territory list has
    // arrived (filterOptions.length > 0) before considering the options "ready".
    // `isLoading` alone is insufficient — React Query can report isLoading=false
    // with an empty result when the cache is stale or the first fetch hasn't
    // resolved yet. Gating on filterOptions (the real territories, not the
    // hardcoded "Not Assigned" placeholder) ensures we always persist the full
    // set, not just [NOT_ASSIGNED_TERRITORY_ID].
    //
    // Recovery: treat a stored sole-[NOT_ASSIGNED_TERRITORY_ID] selection as
    // "not yet initialized" so already-affected users self-heal without having
    // to manually clear the cookie.
    //
    // clearSelection stores [] explicitly so undefined = no cookie, [] = user cleared.
    useEffect(() => {
        if (isLoading)
            return;
        // Real territories must be present before we can persist a meaningful "all" selection.
        if (filterOptions.length === 0)
            return;
        const storedValue = territorySelections[currentSubdomain];
        const hasStoredValue = storedValue !== undefined;
        // Recovery path: a previously-affected user has only [NOT_ASSIGNED_TERRITORY_ID]
        // stored. Treat this as uninitialized so we overwrite it with the full list.
        const isStaleNotAssignedOnly = hasStoredValue && storedValue.length === 1 && storedValue[0] === NOT_ASSIGNED_TERRITORY_ID;
        if (!hasInitializedRef.current && (!hasStoredValue || isStaleNotAssignedOnly)) {
            hasInitializedRef.current = true;
            setMultipleTerritories(allCheckboxOptions.map((opt) => opt.value), currentSubdomain);
        }
    }, [isLoading, filterOptions.length, scopeSettings === null || scopeSettings === void 0 ? void 0 : scopeSettings.territories]);
    if (scopeSettings && !shouldShow)
        return null;
    if (error) {
        console.error('Error loading global scope settings:', error);
        return React.createElement("div", { className: "global-scope-selector-error" }, translate(`error_loading_settings`, { namespace }));
    }
    return (React.createElement("div", { className: "global-scope-selector" },
        React.createElement(Button, { ref: anchor, onClick: handleToggleOpen, disabled: isLoading, mode: "dark" },
            React.createElement("i", { className: "fa-light fa-location-crosshairs" }),
            displayedTerritoryNames.length > 0 && (React.createElement("span", { className: "global-scope-selector__territory-count" }, displayedTerritoryNames.join(', '))),
            territoriesSelectedAmount > 2 && (React.createElement("span", { className: "global-scope-selector__territory-count global-scope-selector__territory-count--overflow" },
                "+",
                territoriesSelectedAmount - 2))),
        React.createElement(Popup, { anchor: anchor.current, show: open, onClose: handleClose, containerClass: "scope-selector-popup" },
            (scopeSettings === null || scopeSettings === void 0 ? void 0 : scopeSettings.companies) && scopeSettings.companies.length > 0 && (React.createElement(Popup.Section, null,
                React.createElement(Popup.Title, null, translate(`instance`, { namespace })),
                React.createElement(Popup.ActionList, null,
                    React.createElement(Button, { ref: instanceAnchor, isMenu: true, onClick: () => setOpenInstanceSelector(true) }, currentSubdomain.toUpperCase())),
                React.createElement(Popup, { anchor: instanceAnchor.current, show: openInstanceSelector, onClose: () => setOpenInstanceSelector(false), position: "right-top", containerClass: "instance-selector-popup" },
                    React.createElement(Popup.Section, null,
                        React.createElement(Popup.Title, null, translate(`change_instance`, { namespace })),
                        React.createElement(Popup.ActionList, null, scopeSettings.companies.map((company) => (React.createElement(Button, { key: company.subdomain, onClick: () => handleCompanySelect(company) },
                            React.createElement("span", { className: "company-info" },
                                React.createElement("span", { className: "company-subdomain-bold" }, company.subdomain.toUpperCase()),
                                React.createElement("span", { className: "company-url" },
                                    company.subdomain,
                                    ".inspectpoint.com")),
                            React.createElement("span", { className: "territories-link" }, getSelectedTerritories(company.subdomain)
                                .map((territoryId) => {
                                var _a;
                                return (_a = ((scopeSettings === null || scopeSettings === void 0 ? void 0 : scopeSettings.territories) || cachedTerritories).find((territory) => territory.id === territoryId)) === null || _a === void 0 ? void 0 : _a.name;
                            })
                                .join(', ')))))))))),
            filterOptions.length > 0 ? (React.createElement(React.Fragment, null,
                React.createElement(Popup.SearchInput, { value: territorySearch, onChange: setTerritorySearch, placeholder: translate('search_territories', { namespace }) }),
                React.createElement(Popup.Split, null),
                React.createElement(Popup.Section, { fillHeight: true },
                    React.createElement(ControlGroup, { controlType: "checkbox", childrenControlType: "checkbox", items: controlGroupItems, value: controlGroupValue, onChange: handleControlGroupChange })))) : (React.createElement(Popup.Section, null,
                React.createElement("div", { className: "empty-state" }, translate(`no_territories_available`, { namespace })))),
            React.createElement(Popup.Split, null),
            React.createElement(Popup.Section, null,
                React.createElement("div", { className: "scope-selector-footer" },
                    territoriesSelectedAmount > 0 && (React.createElement(Button, { className: "reset-button", onClick: handleClearAll }, translate('clear_selection', { namespace }))),
                    React.createElement(Button, { className: "update-button", variant: "primary", disabled: !hasSelectionChanges, onClick: handleUpdate }, translate(`update`, { namespace: 'features.commons' })))))));
}
export default React.memo(GlobalScopeSelector);
